import React, { useCallback, useEffect, useState } from 'react';
import { Paragraph, Pill, PluginLayout, Button, Icon } from '@frontapp/ui-kit';
import { post } from './api.js';
import Input from '@mui/material/Input';
import styles from './styles.module.css';
import { parseMessages } from './util.js';
import Card from '@mui/material/Card';
import Recommendation from './Recommendation.js';
import Typography from '@mui/material/Typography';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import CircularProgress from '@mui/material/CircularProgress';
import { findContacts } from './util.js';
import { track } from '@amplitude/analytics-browser';

const GRADIENT = [
  [0.0, [231, 76, 60]],
  [0.3, [231, 76, 60]],
  [0.65, [252, 245, 95]],
  [0.9, [76, 175, 80]],
  [1.0, [76, 175, 80]],
];

function AgentAssist({ rawMessages, latestInbound, contacts }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [recommendation, setRecommendation] = useState();
  // {context: [], query: string}
  const [similarQuestions, setSimilarQuestions] = useState();

  const getRecommendation = (messages, msgToRespond) => {
    const handles = findContacts(msgToRespond);
    post('resolve', { messages, contact: handles[0] })
      .then((res) => {
        console.log(res);
        if (msgToRespond === latestInbound.current) {
          setRecommendation(res.data.recommendation);
          if (res.data.similarQuestions) {
            setSimilarQuestions(res.data.similarQuestions);
          }
          if (res.data.recommendation.type === 'pending') {
            getGeneratedResponse(res.data.similarQuestions, msgToRespond);
          }
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const getGeneratedResponse = (similarQuestions, msgToRespond) => {
    setLoading(true);
    const handles = findContacts(msgToRespond);
    post('generate', { similarQuestions, contact: handles[0] })
      .then((res) => {
        if (msgToRespond === latestInbound.current) {
          console.log(res);
          setLoading(false);
          if (res.data.recommendation != null) {
            setRecommendation(res.data.recommendation);
          }
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  useEffect(() => {
    setLoading(false);
    setRecommendation(null);
    setSearchQuery('');
    setSearching(false);
    // selected the latest message id from the available messages.
    const messages = parseMessages(rawMessages);
    if (messages != null && messages.length > 0) {
      //  console.log('calling agent assist');
      getRecommendation(messages, latestInbound.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestInbound.current]);

  const getSegment = (value) => {
    const points = GRADIENT.length;
    for (let i = 1; i < points; i++) {
      if (value < GRADIENT[i][0]) {
        return i;
      }
    }
    return points - 1;
  };

  const getColor = (value) => {
    const segment = getSegment(value);
    const leftBound = GRADIENT[segment - 1][0];
    const leftColor = GRADIENT[segment - 1][1];
    const rightBound = GRADIENT[segment][0];
    const rightColor = GRADIENT[segment][1];
    const weight = (value - leftBound) / (rightBound - leftBound);
    const r = Math.round(
      leftColor[0] + weight * (rightColor[0] - leftColor[0]),
    );
    const g = Math.round(
      leftColor[1] + weight * (rightColor[1] - leftColor[1]),
    );
    const b = Math.round(
      leftColor[2] + weight * (rightColor[2] - leftColor[2]),
    );
    return `rgb(${r}, ${g}, ${b})`;
  };

  const Expandable = ({ query }) => {
    const [seeMore, setSeeMore] = useState(false);
    return (
      <div onClick={() => setSeeMore(!seeMore)}>
        <Paragraph>
          <Pill colors={{ backgroundColor: `${getColor(query.similarity)}` }}>
            <b>{query.similarity.toFixed(2)}</b>
          </Pill>
          {seeMore || query.question.length < 160 ? (
            <>
              <b>{query.question}</b>
            </>
          ) : (
            <>
              <b>{query.question.substring(0, 160)}</b>
              <em style={{ color: 'gray' }}>(show more)</em>
            </>
          )}
        </Paragraph>
      </div>
    );
  };

  const search = useCallback((query, currentMsg) => {
    track('SEARCH_SIMILAR_QUESTION_CLICKED');
    setSearching(true);
    post('search', { query })
      .then((res) => {
        setSearching(false);
        console.log(res);
        if (currentMsg === latestInbound.current) {
          setSimilarQuestions(res.data.similarQuestions);
        }
      })
      .catch((error) => {
        setSearching(false);
        console.log(error);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.container}>
      <PluginLayout>
        <>
          {loading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
              }}
            >
              <CircularProgress size={12} />
            </div>
          ) : (
            <Recommendation
              recommendation={recommendation}
              latestInbound={latestInbound}
              contacts={contacts}
            />
          )}

          {similarQuestions != null &&
            recommendation != null &&
            recommendation.type !== 'action' && (
              <>
                <Card
                  variant="outlined"
                  style={{
                    borderRadius: '6px',
                    padding: '12px',
                    marginTop: '12px',
                  }}
                >
                  <div className={styles.searchBar}>
                    <Input
                      value={searchQuery}
                      fullWidth={true}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          search(searchQuery, latestInbound.current);
                        }
                      }}
                      onChange={(t) => {
                        setSearchQuery(t.target.value);
                      }}
                      placeholder="Search a question..."
                    />
                    <Button
                      type="icon"
                      onClick={() => {
                        search(searchQuery, latestInbound.current);
                      }}
                    >
                      <Icon name="Search" />
                    </Button>
                  </div>
                </Card>
                {!searching && similarQuestions.error != null && (
                  <Typography
                    sx={{
                      mt: '12px',
                      fontSize: '0.85rem',
                      lineHeight: 1.2,
                    }}
                  >
                    {similarQuestions.error}
                  </Typography>
                )}
                {!searching &&
                  similarQuestions.context != null &&
                  similarQuestions.context.length > 0 && (
                    <Card
                      variant="outlined"
                      style={{
                        borderRadius: '6px',
                        padding: '12px',
                        marginTop: '12px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                          marginBottom: '12px',
                        }}
                      >
                        <QuestionAnswerIcon
                          sx={{ mr: '8px', color: 'primary.main' }}
                        />
                        <Typography
                          sx={{
                            variant: 'h2',
                            color: 'primary.main',
                            bold: 'true',
                          }}
                        >
                          Similar Questions
                        </Typography>
                      </div>
                      {similarQuestions.context.map((each) => (
                        <div
                          key={each.cnvId}
                          style={{
                            textAlign: 'left',
                            fontSize: '12px',
                            margin: '6px',
                          }}
                          onClick={() => {
                            track('SIMILAR_QUESTIONS_CLICKED');
                          }}
                        >
                          <Expandable query={each} />
                          <Paragraph>{each.answer}</Paragraph>
                          <div
                            style={{ textAlign: 'right', fontStyle: 'italic' }}
                          >
                            <Paragraph>
                              {new Date(each.date).toLocaleString()}
                            </Paragraph>
                          </div>
                        </div>
                      ))}
                    </Card>
                  )}
              </>
            )}
        </>
      </PluginLayout>
    </div>
  );
}

export default AgentAssist;
